import { CertificationProperties } from '@x-guard/xgac-types/xgac';
import moment from 'moment';
import { xgacDataProvider } from '../dataProviders/xgacDataProvider';
import { DocumentAssignee } from './certificationDocuments';
import { CertificationsByAssetId } from './certificationUserForm';

export type AssigneeChoice =
  | { id: string; type: 'user'; name: string }
  | { id: string; type: 'group'; name: string; membersCount: number };

type AssetRef = string | { _id?: string; id?: string };

const toFormDate = (iso: string) => moment.utc(iso).format('YYYY-MM-DD');

export type GroupAppUserInfo = {
  validGroupIds: Set<string>;
  membersCountByGroupId: Map<string, number>;
  appUserIdsByGroupId: Map<string, string[]>;
};

export type CertifiedUsersAssigneeContext = {
  baseChoices: AssigneeChoice[];
  certifiedAssetIdsByCategoryId: Record<string, Set<string>>;
  assetsByCategoryId: Record<string, CertifiedAssetRow[]>;
};

export type CertifiedAssetRow = {
  id?: string | number;
  _id?: string;
  name?: string;
  certifications?: CertificationProperties[];
};

const assetIdOf = (asset: CertifiedAssetRow): string => String(asset.id ?? asset._id);

export const getCertifiedAssetsForCategories = async (
  categoryIds: string[],
): Promise<CertifiedAssetRow[]> => {

  if (categoryIds.length === 0) return [];

  const { data } = await xgacDataProvider.getList('all-assets', {
    pagination: { page: 1, perPage: -1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {
      'certifications.certificationCategory._id': categoryIds.join(','),
    },
    meta: {
      $select: '_id,name,certifications',
    },
  });

  return data as CertifiedAssetRow[];

};

export const partitionCertifiedAssetsByCategory = (
  assets: CertifiedAssetRow[],
  categoryIds: string[],
): {
  certifiedAssetIdsByCategoryId: Record<string, Set<string>>;
  assetsByCategoryId: Record<string, CertifiedAssetRow[]>;
} => {

  const categoryIdSet = new Set(categoryIds);
  const certifiedAssetIdsByCategoryId: Record<string, Set<string>> = Object.fromEntries(
    categoryIds.map((id) => [id, new Set<string>()]),
  );
  const assetsByCategoryId: Record<string, CertifiedAssetRow[]> = Object.fromEntries(
    categoryIds.map((id) => [id, [] as CertifiedAssetRow[]]),
  );

  assets.forEach((asset) => {

    const assetId = assetIdOf(asset);
    const matchedCategoryIds = new Set<string>();

    (asset.certifications ?? []).forEach((certification) => {

      const catId = String(certification.certificationCategory?._id);
      if (!categoryIdSet.has(catId)) return;
      certifiedAssetIdsByCategoryId[catId].add(assetId);
      matchedCategoryIds.add(catId);

    });

    matchedCategoryIds.forEach((catId) => {

      assetsByCategoryId[catId].push(asset);

    });

  });

  return { certifiedAssetIdsByCategoryId, assetsByCategoryId };

};

export const buildAssigneeChoices = (
  users: { id: string | number; name?: string }[],
  groups: { id: string | number; name?: string }[],
  membersCountByGroupId: Map<string, number> = new Map(),
): AssigneeChoice[] => [
  ...groups.map((group) => {

    const groupId = String(group.id);
    return {
      id: groupId,
      type: 'group' as const,
      name: group.name ?? groupId,
      membersCount: membersCountByGroupId.get(groupId) ?? 0,
    };

  }),
  ...users.map((user) => ({
    id: String(user.id),
    type: 'user' as const,
    name: user.name ?? String(user.id),
  })),
];

export const toAssetId = (asset: AssetRef): string | undefined => {

  if (typeof asset === 'string') return asset || undefined;
  const id = asset._id ?? asset.id;
  return id ? String(id) : undefined;

};

const getGroupAssetIds = (group: { assets?: AssetRef[] }): string[] => (group.assets ?? [])
  .map(toAssetId)
  .filter((id): id is string => Boolean(id));

export const splitRecipientSelection = (
  selectedIds: string[],
  choices: AssigneeChoice[],
): { userIds: string[]; groupIds: string[] } => {

  const selectedChoices = selectedIds
    .map((id) => choices.find((choice) => choice.id === id))
    .filter((choice): choice is AssigneeChoice => choice !== undefined);

  return {

    userIds: selectedChoices
      .filter((c): c is Extract<AssigneeChoice, { type: 'user' }> => c.type === 'user')
      .map((c) => c.id),
    groupIds: selectedChoices
      .filter((c): c is Extract<AssigneeChoice, { type: 'group' }> => c.type === 'group')
      .map((c) => c.id),

  };

};

export const getAppUserAssetsByIds = async (assetIds: string[]) => {

  if (assetIds.length === 0) return [];
  const { data } = await xgacDataProvider.getMany('app-users', {
    ids: assetIds,
    meta: { sort: 'name' },
  });
  return data;

};

export const getGroupIdsWithAppUsers = async (
  groups: { id: string | number }[],
): Promise<GroupAppUserInfo> => {

  if (groups.length === 0) return { validGroupIds: new Set(), membersCountByGroupId: new Map(), appUserIdsByGroupId: new Map() };

  const groupIds = groups.map((group) => String(group.id));

  const { data: groupsWithAssets } = await xgacDataProvider.getMany('asset-groups', {
    ids: groupIds,
  });

  const allAssetIds = [...new Set(groupsWithAssets.flatMap(getGroupAssetIds))];

  const users = await getAppUserAssetsByIds(allAssetIds);
  const userAssetIds = new Set(users.map((user) => String(user.id)));

  const validGroupIds = new Set<string>();
  const membersCountByGroupId = new Map<string, number>();
  const appUserIdsByGroupId = new Map<string, string[]>();

  groupsWithAssets.forEach((group) => {

    const memberIds = getGroupAssetIds(group).filter((id) => userAssetIds.has(id));
    appUserIdsByGroupId.set(String(group.id), memberIds);
    const count = memberIds.length;

    if (count > 0) {

      const groupId = String(group.id);
      validGroupIds.add(groupId);
      membersCountByGroupId.set(groupId, count);

    }

  });

  return { validGroupIds, membersCountByGroupId, appUserIdsByGroupId };

};

export const resolveCertifiedAssignees = async (
  userIds: string[],
  groupIds: string[],
  options?: { excludeAssetIds?: Set<string> },
): Promise<DocumentAssignee[]> => {

  const byId = new Map<string, DocumentAssignee>();
  if (userIds.length > 0) {

    const { data: users } = await xgacDataProvider.getMany('app-users', { ids: userIds });

    users.forEach((user) => {

      const id = String(user.id);
      byId.set(id, { id, label: user.name ?? id });

    });

  }

  if (groupIds.length > 0) {

    const { data: groups } = await xgacDataProvider.getMany('asset-groups', { ids: groupIds });

    const assetIds = [...new Set(groups.flatMap(getGroupAssetIds))];

    const users = await getAppUserAssetsByIds(assetIds);

    users.forEach((user) => {

      if (!byId.has(user.id)) {

        const id = String(user.id);
        byId.set(id, { id, label: user.name ?? id });

      }

    });

  }

  const exclude = options?.excludeAssetIds;

  return Array.from(byId.values())
    .filter((assignee) => !exclude?.has(assignee.id))
    .sort((a, b) => a.label.localeCompare(b.label));

};

export const userCertificationTransformer = (data: any) => {

  const categoryId = typeof data.certificationCategory === 'string' ? data.certificationCategory : data.certificationCategory?._id;

  return {
    ...data,
    firstIssuedOn: new Date(data.firstIssuedOn).toISOString(),
    lastReceivedOn: new Date(data.lastReceivedOn).toISOString(),
    expiryDate: new Date(data.expiryDate).toISOString(),
    plannedRenewalDate: data.plannedRenewalDate ? new Date(data.plannedRenewalDate).toISOString() : null,
    certificationCategory: {
      _id: categoryId,
      _ref: 'CertificationCategory',
    },
  };

};

export const buildCertifiedUsersAssigneeContext = async (
  users: { id: string | number; name?: string }[],
  groups: { id: string | number; name?: string }[],
  categoryIds: string[],
): Promise<CertifiedUsersAssigneeContext> => {

  const {
    validGroupIds,
    membersCountByGroupId,
  } = await getGroupIdsWithAppUsers(groups);

  const baseChoices = buildAssigneeChoices(
    users,
    groups.filter((group) => validGroupIds.has(String(group.id))),
    membersCountByGroupId,
  );

  const assets = await getCertifiedAssetsForCategories(categoryIds);
  const {
    certifiedAssetIdsByCategoryId,
    assetsByCategoryId,
  } = partitionCertifiedAssetsByCategory(assets, categoryIds);

  return {
    baseChoices,
    certifiedAssetIdsByCategoryId,
    assetsByCategoryId,
  };

};

export type ExistingCertifiedUser = {
  assetId: string;
  name: string;
  certificationId: string;
  firstIssuedOn: string;
  lastReceivedOn: string;
  expiryDate: string;
  documentaryProof?: string | null;
};

export const buildExistingCertifiedUsers = (
  assets: CertifiedAssetRow[],
  categoryId: string,
): ExistingCertifiedUser[] => (
  assets.flatMap((asset) => {

    const certification = (asset.certifications ?? []).find(
      (c) => String(c.certificationCategory?._id) === categoryId,
    );
    const certificationId = (certification as { _id?: string } | undefined)?._id;

    if (!certificationId || !certification) return [];

    return [{
      assetId: String(asset.id ?? asset._id),
      name: asset.name ?? String(asset.id ?? asset._id),
      certificationId,
      firstIssuedOn: certification.firstIssuedOn,
      lastReceivedOn: certification.lastReceivedOn,
      expiryDate: certification.expiryDate,
      documentaryProof: certification.documentaryProof ?? null,
    }];

  })
);

export const buildInitialCertificationsByAssetId = (
  existingUsers: ExistingCertifiedUser[],
): CertificationsByAssetId => Object.fromEntries(
  existingUsers.map((user) => [
    user.assetId,
    {
      firstIssuedOn: toFormDate(user.firstIssuedOn),
      lastReceivedOn: toFormDate(user.lastReceivedOn),
      expiryDate: toFormDate(user.expiryDate),
      ...(user.documentaryProof
        ? { document: { src: user.documentaryProof } }
        : {}),
    },
  ]),
);
